<script setup>
import { computed, ref, onMounted } from "vue";
import { useRouter } from "vue-router";
import { currentUser, logout, updateUser, getUserEffectivePermissions, listModules } from "./db/auth.js";
import Swal from "sweetalert2";
import { listen } from "@tauri-apps/api/event";
import CartSidebar from "./components/CartSidebar.vue";
import { cartState, cartCount } from "./store/cart.js";
import { listInventory } from "./db/inventory.js";
import { subscribeToStockAlerts, listLowStockAlerts } from "./db/ingredients.js";

// Debug: Verificar que el código se está recargando
console.log(`🔥 APP LOADED - ${new Date().toLocaleTimeString()} - VERSION: ${Date.now()}`);

const router = useRouter();
const user = ref(currentUser());
const menuOpen = ref(false);
const mobileNavOpen = ref(false);
const themeMode = ref(
  user.value?.theme_mode || localStorage.getItem("dev_theme_mode") || null
);

// Detectar si estamos en una ruta pública (sin navbar)
const isPublicRoute = computed(() => {
  const path = router.currentRoute.value?.path || '/';
  const publicPaths = ['/', '/login', '/register', '/activate', '/forgot-password', '/reset-password'];
  return publicPaths.includes(path);
});

function applyTheme(mode) {
  let m = mode;
  if (!m) {
    const sysLight =
      typeof window !== "undefined" &&
      window.matchMedia &&
      window.matchMedia("(prefers-color-scheme: light)").matches;
    m = sysLight ? "light" : "dark";
  }
  document.documentElement.setAttribute("data-theme", m);
  themeMode.value = m;
}
applyTheme(themeMode.value);

function refreshUser() {
  user.value = currentUser();
  loadPermissions();
}

if (typeof window !== "undefined") {
  window.addEventListener("session-updated", () => {
    refreshUser();
    applyTheme(currentUser()?.theme_mode || themeMode.value);
  });
  window.addEventListener("storage", () => {
    refreshUser();
    applyTheme(currentUser()?.theme_mode || themeMode.value);
  });
}

function doLogout() {
  logout();
  user.value = null;
  router.push("/");
}

function initials(u) {
  const base = (u?.full_name || u?.username || "U").trim();
  const parts = base.split(/\s+/).filter(Boolean);
  const first = parts[0]?.[0] || "U";
  const second = parts.length > 1 ? parts[1]?.[0] || "" : "";
  return (first + second).toUpperCase();
}

function openProfile() {
  router.push("/perfil");
}

async function toggleTheme() {
  const next = themeMode.value === "dark" ? "light" : "dark";
  applyTheme(next);
  try {
    if (user.value) {
      await updateUser(user.value.id, { theme_mode: next });
    } else {
      localStorage.setItem("dev_theme_mode", next);
    }
  } catch { }
}

const cartBadge = computed(() => cartCount());
function openCart() {
  cartState.open = true;
}

const notifOpen = ref(false);
const notifications = ref([]);
const notifCount = computed(() => notifications.value.length);
function toggleNotif() {
  notifOpen.value = !notifOpen.value;
  if (notifOpen.value) {
    refreshLowStockNotifications();
  }
}
function clearNotifications() {
  notifications.value = [];
  notifOpen.value = false;
  refreshLowStockNotifications();
}
function addNotification(p) {
  const name = String((p && (p.name || p.ingredient_name)) || "Ingrediente");
  const init = Number((p && p.initial) || 0);
  const cur = Number((p && p.current) || 0);
  const perc = init > 0 ? Math.round((cur / init) * 100) : 0;
  const msg = `Stock bajo: ${name} (${perc}% de ${init})`;
  notifications.value = [{ message: msg, at: new Date().toISOString() }, ...notifications.value].slice(0, 20);
}

const canInstall = ref(false);
let deferredPrompt = null;
const scrollProgress = ref(0);
const updateScrollProgress = () => {
  const totalHeight = document.documentElement.scrollHeight - window.innerHeight;
  if (totalHeight > 0) {
    scrollProgress.value = (window.scrollY / totalHeight) * 100;
  }
};

onMounted(async () => {
  updateScrollProgress();
  window.addEventListener('scroll', updateScrollProgress);
  try {
    window.addEventListener('beforeinstallprompt', (e) => {
      e.preventDefault();
      deferredPrompt = e;
      canInstall.value = true;
    });
  } catch { }
  try {
    if (typeof window !== "undefined") {
      window.addEventListener("ops:stock:low", (e) => {
        const d = e?.detail || e;
        addNotification(d);
        refreshLowStockNotifications();
      });
    }
  } catch {}
  try {
    subscribeToStockAlerts(addNotification);
  } catch {}
  // try {
  //   await listen("debug-error", (e) => {
  //     const p = e?.payload;
  //     const msg = typeof p === "string" ? p : (p && (p.message || p.area)) || "";
  //     debugMsg.value = String(msg || "");
  //     debugVisible.value = !!debugMsg.value;
  //   });
  // } catch {}
  // try {
  //   if (typeof window !== "undefined") {
  //     window.addEventListener("debug-error", (e) => {
  //       const d = e?.detail || e;
  //       const msg = (d && (d.message || d.area)) || "";
  //       debugMsg.value = String(msg || "");
  //       debugVisible.value = !!debugMsg.value;
  //     });
  //   }
  // } catch {}
});

async function refreshLowStockNotifications() {
  try {
    const lows = await listLowStockAlerts(0.5);
    const pinned = (lows || []).map((p) => {
      const msg = `Stock bajo: ${String(p.name || "Ingrediente")} (${Number(p.percent || 0)}% de ${Number(p.initial || 0)})`;
      return { message: msg, at: new Date().toISOString() };
    });
    notifications.value = pinned.length ? pinned : notifications.value;
  } catch {}
}

async function installApp() {
  try {
    if (!deferredPrompt) return;
    const dp = deferredPrompt;
    deferredPrompt = null;
    await dp.prompt();
    await dp.userChoice;
    canInstall.value = false;
  } catch { }
}



// --- Permisos y bloqueo de navegación ---
const allowedSet = ref(new Set()); // códigos de módulos con acceso

async function loadPermissions() {
  try {
    const u = currentUser();
    if (!u) {
      allowedSet.value = new Set();
      return;
    }
    const role = String(u.role || '').toLowerCase();
    const isAdmin = ["admin", "superadmin"].includes(role) || u?.role_id === 1;
    // Admin y Superadmin: acceso completo a todos los módulos
    if (isAdmin) {
      try {
        const mods = await listModules();
        const list = (mods && mods.length) ? mods : [
          { code: 'dashboard' },
          { code: 'inventory' },
          { code: 'sales' },
          { code: 'purchases' },
          { code: 'customers' },
          { code: 'menu' },
          { code: 'orders' },
          { code: 'preparation' },
          { code: 'products' },
          { code: 'users' },
        ];
        allowedSet.value = new Set(list.map(m => String(m.code)));
        return;
      } catch { }
    }
    const rows = await getUserEffectivePermissions(u.id);
    allowedSet.value = new Set(rows.filter((r) => r.can_access === 1).map((r) => String(r.code)));
    // Fallback visual para cliente: asegurar mínimos en nav
    const roleName = String(u.role || u.role_name || '').toLowerCase();
    if (roleName === 'cliente') {
      ['dashboard', 'products', 'purchases', 'menu'].forEach((c) => allowedSet.value.add(c));
    } else {
      // El Dashboard debe estar siempre accesible para usuarios autenticados
      allowedSet.value.add('dashboard');
    }
  } catch {
    allowedSet.value = new Set();
  }
}

onMounted(loadPermissions);

function canAccess(code) {
  return allowedSet.value.has(String(code));
}

function go(path, code) {
  const role = String(currentUser()?.role || '').toLowerCase();
  const isAdmin = ['admin', 'superadmin'].includes(role) || currentUser()?.role_id === 1;
  if (!isAdmin && !canAccess(code)) {
    Swal.fire({
      icon: "info",
      title: "Acceso bloqueado",
      text: "No tienes permisos para acceder a esta opción.",
      confirmButtonText: "Entendido",
    });
    return;
  }
  router.push(path);
}

// --- Búsqueda de productos desde la nav ---
const productSearch = ref("");
const showProductSuggestions = ref(false);
const hoverProductIndex = ref(-1);
const allProducts = ref([]);
const productSuggestions = ref([]);
let searchTimer = null;

async function ensureProductsLoaded() {
  try {
    if (allProducts.value.length) return;
    const rows = await listInventory();
    allProducts.value = (rows || []).map((r) => ({
      id: r.id,
      name: r.name || "",
      sku: r.sku || "",
      image_base64: r.image_base64 || null,
      image_mime: r.image_mime || "image/jpeg",
    }));
  } catch { }
}

function updateProductSuggestions() {
  const q = productSearch.value.trim().toLowerCase();
  const base = !q
    ? allProducts.value.slice()
    : allProducts.value.filter(
      (p) => (p.name || "").toLowerCase().includes(q) || (p.sku || "").toLowerCase().includes(q)
    );
  productSuggestions.value = base.slice(0, 8);
}

function emitProductSearch() {
  const q = productSearch.value.trim();
  if (searchTimer) clearTimeout(searchTimer);
  searchTimer = setTimeout(() => {
    try {
      if (typeof window !== "undefined") {
        window.dispatchEvent(new CustomEvent("product-search", { detail: q }));
      }
    } catch { }
  }, 250);
}
function ensureProductsRoute() {
  try {
    const cur = router.currentRoute.value?.path || "";
    if (cur !== "/productos") router.push("/productos");
  } catch { }
}

async function onNavSearchFocus() {
  showProductSuggestions.value = true;
  await ensureProductsLoaded();
  updateProductSuggestions();
}
function onNavSearchBlur() {
  setTimeout(() => (showProductSuggestions.value = false), 180);
}
async function onNavSearchInput() {
  await ensureProductsRoute();
  emitProductSearch();
  await ensureProductsLoaded();
  updateProductSuggestions();
  showProductSuggestions.value = true;
}
function onNavSearchKeydown(e) {
  if (!showProductSuggestions.value || !productSuggestions.value.length) return;
  if (e.key === "ArrowDown") {
    e.preventDefault();
    hoverProductIndex.value = (hoverProductIndex.value + 1) % productSuggestions.value.length;
  } else if (e.key === "ArrowUp") {
    e.preventDefault();
    hoverProductIndex.value = (hoverProductIndex.value - 1 + productSuggestions.value.length) % productSuggestions.value.length;
  } else if (e.key === "Enter") {
    e.preventDefault();
    const row = productSuggestions.value[hoverProductIndex.value >= 0 ? hoverProductIndex.value : 0];
    if (row) selectNavSuggestion(row);
  } else if (e.key === "Escape") {
    showProductSuggestions.value = false;
  }
}
function selectNavSuggestion(p) {
  productSearch.value = p.name || p.sku || "";
  emitProductSearch();
  ensureProductsRoute();
  showProductSuggestions.value = false;
}
</script>

<template>
  <main class="shell" :class="{ 'shell-no-nav': isPublicRoute }">
    <div class="scroll-progress" v-if="!isPublicRoute" :style="{ width: scrollProgress + '%' }"></div>
    <header class="topbar" v-if="!isPublicRoute">
      <div class="brand">
        <img src="/img/sushi.png" alt="" class="brand-logo" />
        <span class="logo-text" style="font-size: 1.5rem;">Sushito</span>
        <!-- Botón hamburguesa para móviles -->
        <button class="hamburger-btn" @click="mobileNavOpen = !mobileNavOpen" aria-label="Menu">
          <span class="material-symbols-rounded">{{ mobileNavOpen ? 'close' : 'menu' }}</span>
        </button>
      </div>
      <nav class="nav" :class="{ 'nav-mobile-open': mobileNavOpen }">
        <router-link to="/dashboard" class="nav-link" :class="{ disabled: !canAccess('dashboard') }"
          :aria-disabled="!canAccess('dashboard')" :tabindex="canAccess('dashboard') ? 0 : -1"
          @click.prevent="go('/dashboard', 'dashboard'); mobileNavOpen = false">
          <span class="material-symbols-rounded" aria-hidden="true">space_dashboard</span>
          Inicio
        </router-link>
        <router-link to="/inventario" class="nav-link" :class="{ disabled: !canAccess('inventory') }"
          :aria-disabled="!canAccess('inventory')" :tabindex="canAccess('inventory') ? 0 : -1"
          @click.prevent="go('/inventario', 'inventory'); mobileNavOpen = false">
          <span class="material-symbols-rounded" aria-hidden="true">inventory_2</span>
          Inventario
        </router-link>
        <router-link to="/productos" class="nav-link" :class="{ disabled: !canAccess('products') }"
          :aria-disabled="!canAccess('products')" :tabindex="canAccess('products') ? 0 : -1"
          @click.prevent="go('/productos', 'products'); mobileNavOpen = false">
          <span class="material-symbols-rounded" aria-hidden="true">category</span>
          Productos
        </router-link>
        <router-link to="/ventas" class="nav-link" :class="{ disabled: !canAccess('sales') }"
          :aria-disabled="!canAccess('sales')" :tabindex="canAccess('sales') ? 0 : -1"
          @click.prevent="go('/ventas', 'sales'); mobileNavOpen = false">
          <span class="material-symbols-rounded" aria-hidden="true">point_of_sale</span>
          Ventas
        </router-link>
        <router-link to="/usuarios" class="nav-link" :class="{ disabled: !canAccess('users') }"
          :aria-disabled="!canAccess('users')" :tabindex="canAccess('users') ? 0 : -1"
          @click.prevent="go('/usuarios', 'users'); mobileNavOpen = false">
          <span class="material-symbols-rounded" aria-hidden="true">group</span>
          Usuarios
        </router-link>
        <!-- Barra de búsqueda moderna para productos -->
        <div class="nav-search" title="Buscar productos">
          <span class="material-symbols-rounded search-icon" aria-hidden="true">search</span>
          <input class="nav-input" v-model="productSearch" placeholder="Buscar productos" @input="onNavSearchInput"
            @focus="() => { ensureProductsRoute(); onNavSearchFocus(); }" @blur="onNavSearchBlur"
            @keydown="onNavSearchKeydown" @keydown.enter.prevent="ensureProductsRoute" />
          <button v-if="productSearch" class="clear-btn" @click="productSearch = ''; emitProductSearch()"
            aria-label="Limpiar búsqueda">
            <span class="material-symbols-rounded" aria-hidden="true">close</span>
          </button>
          <div class="nav-suggestions" v-if="showProductSuggestions && productSuggestions.length">
            <button v-for="(s, idx) in productSuggestions" :key="s.id" class="suggestion-item"
              :class="{ active: idx === hoverProductIndex }" type="button" @mouseenter="hoverProductIndex = idx"
              @mousedown.prevent="selectNavSuggestion(s)">
              <img v-if="s.image_base64" class="suggestion-thumb"
                :src="`data:${s.image_mime || 'image/jpeg'};base64,${s.image_base64}`" alt="" />
              <span class="suggestion-name">{{ s.name }}</span>
              <span class="suggestion-sku">{{ s.sku || '-' }}</span>
            </button>
          </div>
        </div>
        <button class="nav-link" @click="toggleTheme(); mobileNavOpen = false" aria-label="Cambiar tema">
          <span class="material-symbols-rounded">{{
            themeMode === "dark" ? "light_mode" : "dark_mode"
            }}</span>
        </button>
        <div class="notif-wrapper">
          <button class="nav-link cart-btn" aria-label="Notificaciones" title="Notificaciones" :aria-expanded="notifOpen" @click="toggleNotif(); mobileNavOpen = false">
            <span class="material-symbols-rounded" aria-hidden="true">notifications</span>
            <span class="badge" v-if="notifCount > 0">{{ notifCount }}</span>
          </button>
          <div v-if="notifOpen" class="notif-dropdown">
            <div class="notif-head">
              <b>Notificaciones</b>
              <button class="drop-item" @click="clearNotifications">Limpiar</button>
            </div>
            <ul class="notif-list">
              <li v-for="n in notifications" :key="n.at" class="notif-item">
                <span class="material-symbols-rounded">warning</span>
                <span class="notif-text">{{ n.message }}</span>
              </li>
              <li v-if="!notifications.length" class="notif-empty">Sin notificaciones</li>
            </ul>
          </div>
        </div>
        <button v-if="canInstall" class="nav-link" @click="installApp(); mobileNavOpen = false"
          title="Instalar aplicación">
          <span class="material-symbols-rounded" aria-hidden="true">install_mobile</span>
          Instalar
        </button>
        <button class="nav-link cart-btn" @click="openCart(); mobileNavOpen = false" aria-label="Abrir carrito">
          <span class="material-symbols-rounded" aria-hidden="true">shopping_cart</span>
          <span class="badge" v-if="cartBadge > 0">{{ cartBadge }}</span>
        </button>
        <div v-if="user" class="user-menu">
          <button class="avatar-btn" @click="menuOpen = !menuOpen" aria-haspopup="true" :aria-expanded="menuOpen">
            <img v-if="user.avatar_url" :src="user.avatar_url" alt="avatar" class="avatar-img" />
            <span v-else class="avatar-fallback">{{ initials(user) }}</span>
          </button>
          <span class="user-name">{{ user.full_name || user.username }}</span>
          <div v-if="menuOpen" class="dropdown">
            <button class="drop-item" @click="
              openProfile();
            menuOpen = false;
            mobileNavOpen = false;
            ">
              <span class="material-symbols-rounded" aria-hidden="true">person</span>
              Perfil
            </button>
            <button class="drop-item danger" @click="
              doLogout();
            menuOpen = false;
            mobileNavOpen = false;
            ">
              <span class="material-symbols-rounded" aria-hidden="true">logout</span>
              Cerrar sesión
            </button>
          </div>
        </div>
        <router-link v-else to="/login" class="nav-link" @click="mobileNavOpen = false">Entrar</router-link>
      </nav>
      <!-- Overlay para cerrar el menú en móvil -->
      <div v-if="mobileNavOpen" class="mobile-overlay" @click="mobileNavOpen = false"></div>
    </header>
    <router-view :key="$route.fullPath" />
    <CartSidebar />
    <!-- <div v-if="debugVisible" class="debug-overlay">
      <span class="debug-text">{{ debugMsg }}</span>
      <button class="debug-close" @click="debugVisible = false" aria-label="Cerrar">✕</button>
    </div> -->
  </main>
</template>

<style>
/* Paleta global (no scoped) para variables usadas en todos los componentes */
:root {
  --bg: #0b0f14;
  --surface: rgba(255, 255, 255, 0.06);
  --surface-2: rgba(255, 255, 255, 0.08);
  --surface-3: rgba(255, 255, 255, 0.10);
  --border: rgba(255, 255, 255, 0.12);
  --text: #e6e8eb;
  --muted: #9aa3af;
  --accent: #7c5cff;
  /* púrpura moderno */
  --accent-2: #00c2a8;
  /* turquesa moderno */
  --accent-gradient: var(--accent);
  --exotic-gradient-soft: rgba(255, 255, 255, 0.08);
  --icon-color: #ffffff;
  /* Paneles flotantes (dropdown, popovers, etc.) */
  --panel-bg: rgba(20, 24, 30, 0.96);
  --panel-border: rgba(255, 255, 255, 0.18);
}

/* Overrides manuales por preferencia del usuario */
:root[data-theme="dark"] {
  --bg: #0b0f14;
  --surface: rgba(255, 255, 255, 0.12);
  --surface-2: rgba(255, 255, 255, 0.10);
  --surface-3: rgba(255, 255, 255, 0.14);
  --border: rgba(255, 255, 255, 0.22);
  --text: #f1f5f9;
  --muted: #cbd5e1;
  --accent: #7c5cff;
  --accent-2: #00c2a8;
  --accent-gradient: var(--accent);
  --exotic-gradient-soft: rgba(255, 255, 255, 0.10);
  --icon-color: #ffffff;
  --panel-bg: rgba(20, 24, 30, 0.96);
  --panel-border: rgba(255, 255, 255, 0.18);
}

:root[data-theme="light"] {
  --bg: #f8fafc;
  --surface: #ffffff;
  --surface-2: #f1f5f9;
  --surface-3: #e2e8f0;
  --border: #e6e8ee;
  --text: #0f172a;
  --muted: #64748b;
  --accent: #7c5cff;
  --accent-2: #00c2a8;
  --accent-gradient: var(--accent);
  --exotic-gradient-soft: #f1f5f9;
  --icon-color: #1a1f29;
  --panel-bg: #ffffff;
  --panel-border: #e6e8ee;
}

@media (prefers-color-scheme: light) {
  :root {
    --bg: #f8fafc;
    --surface: #ffffff;
    --surface-2: #f1f5f9;
    --surface-3: #e2e8f0;
    --border: #e6e8ee;
    --text: #0f172a;
    --muted: #64748b;
    --accent: #7c5cff;
    /* botón principal púrpura */
    --accent-2: #00c2a8;
    /* turquesa éxito */
    --accent-gradient: var(--accent);
    --exotic-gradient-soft: #f1f5f9;
    --icon-color: #1a1f29;
    --panel-bg: #ffffff;
    --panel-border: #e6e8ee;
  }
}

/* Tipografía y base */
html,
body {
  font-family: "Outfit", system-ui, -apple-system, Segoe UI, Roboto, Helvetica,
    Arial, "Noto Sans", "Apple Color Emoji", "Segoe UI Emoji";
  letter-spacing: 0.2px;
  margin: 0;
  /* evita el borde blanco por margen por defecto */
  background: var(--bg);
  /* asegura fondo consistente a nivel documento */
}

body {
  line-height: 1.25;
  max-width: 100%;
  overflow-x: hidden;
  /* evita desbordes horizontales globales */
}

/* el contenedor raíz también hereda el fondo para cubrir toda el área */
#app {
  min-height: 100vh;
  background: var(--bg);
}

/* Mejora legibilidad de iconos Material Symbols */
.material-symbols-rounded {
  font-variation-settings: "FILL" 0, "wght" 600, "GRAD" 0, "opsz" 48;
}

/* Esquema global: asegura que los controles nativos usen el tema correcto */
:root[data-theme="dark"] {
  color-scheme: dark;
}

:root[data-theme="light"] {
  color-scheme: light;
}

/* Estilos globales para el dropdown de los filtros en modo oscuro */
@media (prefers-color-scheme: dark) {

  .filters select option,
  .filters select optgroup {
    background-color: #0f172a;
    color: #e5e7eb;
  }

  .filters select option:hover,
  .filters select option:checked {
    background-color: #1f2937;
    color: #ffffff;
  }
}

/* Si el tema oscuro se fuerza vía data-theme, replicar los estilos */
:root[data-theme="dark"] .filters select option,
:root[data-theme="dark"] .filters select optgroup {
  background-color: #0f172a;
  color: #e5e7eb;
}

:root[data-theme="dark"] .filters select option:hover,
:root[data-theme="dark"] .filters select option:checked {
  background-color: #1f2937;
  color: #ffffff;
}
.logo-text {
  font-family: 'Dancing Script', cursive !important;
  font-weight: 700 !important;
  background: linear-gradient(135deg, #FFD700 0%, #C6923D 100%);
  -webkit-background-clip: text !important;
  -webkit-text-fill-color: transparent !important;
  background-clip: text !important;
  letter-spacing: 0.05em !important;
  white-space: nowrap;
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.3));
  text-transform: none !important;
}

.scroll-progress {
  position: fixed;
  top: 0;
  left: 0;
  height: 4px;
  background: linear-gradient(90deg, #FFD700, #C6923D, #DFC05E);
  z-index: 9999;
  transition: width 0.15s ease-out;
  box-shadow: 0 0 15px rgba(255, 215, 0, 0.6);
  pointer-events: none;
}
</style>

<style scoped>
.shell {
  min-height: 100vh;
  color: var(--text);
  background: var(--bg);
  overflow-x: hidden;
  padding-top: 68px;
}

.shell-no-nav {
  padding-top: 0;
}

.shell-brown {
  background: #3b2a22 !important;
  background-image: none !important;
}

.debug-overlay {
  position: fixed;
  right: 16px;
  bottom: 16px;
  z-index: 4000;
  background: rgba(239, 68, 68, 0.12);
  border: 1px solid #ef4444;
  color: #ef4444;
  padding: 0.75rem 1rem;
  border-radius: 10px;
  display: inline-flex;
  gap: 0.6rem;
  align-items: center;
  box-shadow: 0 18px 28px rgba(0, 0, 0, 0.18);
}

.debug-text {
  font-weight: 700;
}

.debug-close {
  border: none;
  background: transparent;
  color: inherit;
  font-weight: 800;
  cursor: pointer;
}

.topbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem 1.25rem;
  border-bottom: 1px solid rgba(198, 146, 61, 0.3);
  background: linear-gradient(135deg, #0a0b14 0%, #1a1610 100%);
  backdrop-filter: blur(16px) saturate(180%);
  box-shadow:
    0 1px 3px rgba(0, 0, 0, 0.3),
    0 8px 24px rgba(0, 0, 0, 0.2);
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 2000;
  transition: all 0.3s ease;
}

.brand {
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
}

.brand-logo {
  height: 28px;
  width: auto;
  display: block;
  transition: transform 0.3s ease;
}

.brand-logo:hover {
  transform: scale(1.05);
}

.nav {
  display: inline-flex;
  gap: 0.4rem;
  align-items: center;
  flex-wrap: wrap;
}

.nav-link {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  padding: 0.5rem 0.85rem;
  border-radius: 10px;
  border: 1px solid transparent;
  background: transparent;
  text-decoration: none;
  color: rgba(255, 255, 255, 0.9);
  font-weight: 500;
  font-size: 0.95rem;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  cursor: pointer;
  position: relative;
}

.nav-link .material-symbols-rounded {
  color: rgba(255, 255, 255, 0.9);
  font-size: 1.1rem;
  transition: transform 0.25s ease;
}

.nav-link:hover {
  background: linear-gradient(135deg,
      rgba(124, 92, 255, 0.12) 0%,
      rgba(0, 194, 168, 0.08) 100%);
  border-color: rgba(124, 92, 255, 0.2);
  box-shadow:
    0 4px 12px rgba(124, 92, 255, 0.15),
    0 2px 4px rgba(0, 0, 0, 0.05);
  transform: translateY(-1px);
}

.nav-link:hover .material-symbols-rounded {
  transform: scale(1.1);
  color: #DFC05E;
}

.nav-link.router-link-active {
  background: linear-gradient(135deg,
      rgba(198, 146, 61, 0.25) 0%,
      rgba(223, 192, 94, 0.2) 100%);
  border-color: rgba(198, 146, 61, 0.5);
  box-shadow:
    0 4px 16px rgba(198, 146, 61, 0.3),
    0 2px 6px rgba(0, 0, 0, 0.2);
  font-weight: 600;
  color: #DFC05E;
}

.nav-link.disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.nav-link.disabled:hover {
  background: transparent;
  border-color: transparent;
  box-shadow: none;
  transform: none;
}

/* User avatar + dropdown */
.user-menu {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 0.65rem;
}

.avatar-btn {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: 2px solid rgba(198, 146, 61, 0.4);
  background: rgba(20, 24, 30, 0.8);
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  overflow: hidden;
}

.avatar-btn:hover {
  border-color: rgba(198, 146, 61, 0.7);
  box-shadow:
    0 4px 12px rgba(198, 146, 61, 0.3),
    0 2px 6px rgba(0, 0, 0, 0.2);
  transform: translateY(-2px) scale(1.05);
}

.avatar-img {
  position: absolute;
  inset: 0;
  display: block;
  width: 100%;
  height: 100%;
  border-radius: 50%;
  object-fit: cover;
  object-position: center;
}

.avatar-fallback {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  aspect-ratio: 1 / 1;
  border-radius: 50%;
  font-weight: 700;
  font-size: 0.9rem;
  color: #DFC05E;
  background: linear-gradient(135deg,
      rgba(198, 146, 61, 0.3) 0%,
      rgba(223, 192, 94, 0.25) 100%);
}

.user-name {
  color: rgba(255, 255, 255, 0.9);
  font-weight: 600;
  font-size: 0.95rem;
  letter-spacing: 0.01em;
  max-width: 220px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.dropdown {
  position: absolute;
  right: 0;
  top: 48px;
  min-width: 200px;
  padding: 0.5rem;
  border-radius: 14px;
  border: 1px solid var(--panel-border);
  background: var(--panel-bg);
  box-shadow:
    0 8px 32px rgba(0, 0, 0, 0.12),
    0 4px 16px rgba(0, 0, 0, 0.08);
  backdrop-filter: blur(16px);
  display: grid;
  gap: 0.3rem;
  z-index: 9999;
  animation: dropdownFadeIn 0.2s ease-out;
}

.notif-wrapper { position: relative; }
.notif-dropdown {
  position: absolute;
  right: 0;
  top: 48px;
  min-width: 280px;
  padding: 0.5rem;
  border-radius: 14px;
  border: 1px solid var(--panel-border);
  background: var(--panel-bg);
  box-shadow: 0 8px 32px rgba(0,0,0,0.12), 0 4px 16px rgba(0,0,0,0.08);
  backdrop-filter: blur(16px);
  display: grid;
  gap: 0.4rem;
  z-index: 9999;
  animation: dropdownFadeIn 0.2s ease-out;
}
.notif-head { display: flex; align-items: center; justify-content: space-between; }
.notif-list { list-style: none; padding: 0; margin: 0; display: grid; gap: 0.35rem; }
.notif-item { display: inline-flex; align-items: center; gap: 0.5rem; padding: 0.5rem; border-radius: 10px; }
.notif-item .material-symbols-rounded { color: #f59e0b; }
.notif-empty { color: var(--muted); padding: 0.5rem; text-align: center; }

@keyframes dropdownFadeIn {
  from {
    opacity: 0;
    transform: translateY(-8px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.drop-item {
  display: inline-flex;
  align-items: center;
  gap: 0.6rem;
  width: 100%;
  padding: 0.6rem 0.75rem;
  border-radius: 10px;
  border: 1px solid transparent;
  background: transparent;
  color: var(--text);
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
}

.drop-item .material-symbols-rounded {
  font-size: 1.1rem;
}

.drop-item:hover {
  background: linear-gradient(135deg,
      rgba(198, 146, 61, 0.2) 0%,
      rgba(223, 192, 94, 0.15) 100%);
  border-color: rgba(198, 146, 61, 0.4);
  transform: translateX(2px);
}

.drop-item.danger {
  color: #ff6b6b;
}

.drop-item.danger:hover {
  background: rgba(255, 77, 79, 0.12);
  border-color: rgba(255, 77, 79, 0.3);
}

.cart-btn {
  position: relative;
}

.badge {
  position: absolute;
  top: -4px;
  right: -4px;
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
  color: white;
  font-size: 0.7rem;
  font-weight: 700;
  line-height: 1;
  padding: 3px 6px;
  border-radius: 999px;
  box-shadow:
    0 2px 8px rgba(239, 68, 68, 0.4),
    0 1px 3px rgba(0, 0, 0, 0.2);
  animation: badgePulse 2s ease-in-out infinite;
}

@keyframes badgePulse {

  0%,
  100% {
    transform: scale(1);
  }

  50% {
    transform: scale(1.1);
  }
}

/* Search bar */
.nav-search {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  margin-left: 0.5rem;
}

.nav-input {
  width: 260px;
  max-width: 32vw;
  padding: 0.55rem 2.2rem 0.55rem 2.5rem;
  border-radius: 12px;
  border: 1px solid rgba(124, 92, 255, 0.15);
  background: linear-gradient(135deg,
      rgba(255, 255, 255, 0.05) 0%,
      rgba(124, 92, 255, 0.03) 100%);
  color: var(--text);
  font-size: 0.95rem;
  outline: none;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
}

.nav-input::placeholder {
  color: rgba(255, 255, 255, 0.5);
  font-weight: 400;
}

.nav-input:focus {
  background: rgba(20, 24, 30, 0.8);
  box-shadow:
    0 4px 16px rgba(198, 146, 61, 0.3),
    0 2px 8px rgba(0, 0, 0, 0.2);
  border-color: rgba(198, 146, 61, 0.6);
}

.nav-search .search-icon {
  position: absolute;
  left: 12px;
  pointer-events: none;
  font-size: 1.1rem;
  color: rgba(255, 255, 255, 0.6);
  transition: color 0.25s ease;
}

.nav-input:focus~.search-icon {
  color: #DFC05E;
}

.nav-search .clear-btn {
  position: absolute;
  right: 8px;
  border: none;
  background: transparent;
  color: rgba(255, 255, 255, 0.6);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  padding: 4px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.nav-search .clear-btn:hover {
  color: rgba(255, 255, 255, 0.9);
  background: rgba(198, 146, 61, 0.2);
  transform: scale(1.1);
}

.nav-suggestions {
  position: absolute;
  left: 0;
  top: calc(100% + 8px);
  width: min(440px, 70vw);
  background: var(--panel-bg);
  border: 1px solid var(--panel-border);
  border-radius: 14px;
  box-shadow:
    0 12px 32px rgba(0, 0, 0, 0.15),
    0 4px 16px rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(16px);
  padding: 0.4rem;
  z-index: 1000;
  animation: dropdownFadeIn 0.2s ease-out;
}

.nav-suggestions .suggestion-item {
  display: grid;
  grid-template-columns: 32px 1fr auto;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.6rem;
  border-radius: 10px;
  border: 1px solid transparent;
  background: transparent;
  color: var(--text);
  cursor: pointer;
  transition: all 0.2s ease;
}

.nav-suggestions .suggestion-item.active,
.nav-suggestions .suggestion-item:hover {
  background: linear-gradient(135deg,
      rgba(124, 92, 255, 0.12) 0%,
      rgba(0, 194, 168, 0.08) 100%);
  border-color: rgba(124, 92, 255, 0.25);
  transform: translateX(2px);
}

.nav-suggestions .suggestion-thumb {
  width: 32px;
  height: 32px;
  object-fit: cover;
  border-radius: 8px;
  border: 1px solid rgba(198, 146, 61, 0.3);
}

.nav-suggestions .suggestion-name {
  font-weight: 600;
  font-size: 0.95rem;
}

.nav-suggestions .suggestion-sku {
  color: var(--muted);
  font-size: 0.85rem;
  font-weight: 500;
}

/* Responsive */
@media (max-width: 1000px) {
  .topbar {
    flex-direction: column;
    align-items: stretch;
    gap: 0.6rem;
    padding: 0.75rem 1rem;
  }

  .shell {
    padding-top: 140px;
  }

  .brand {
    justify-content: space-between;
  }

  .nav {
    justify-content: flex-start;
    row-gap: 0.4rem;
  }

  .nav-input {
    width: 200px;
  }

  .nav-search {
    margin-left: 0;
  }
}

@media (max-width: 640px) {
  .nav-link {
    padding: 0.45rem 0.7rem;
    font-size: 0.9rem;
  }

  .nav-link .material-symbols-rounded {
    font-size: 1rem;
  }

  .user-name {
    display: none;
  }

  .nav-input {
    width: 160px;
    font-size: 0.9rem;
  }

  .brand-logo {
    height: 24px;
  }
}

/* Hamburger button - solo visible en móvil */
.hamburger-btn {
  display: none;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 10px;
  border: 1px solid rgba(198, 146, 61, 0.4);
  background: transparent;
  color: rgba(255, 255, 255, 0.9);
  cursor: pointer;
  transition: all 0.25s ease;
}

.hamburger-btn:hover {
  background: linear-gradient(135deg,
      rgba(198, 146, 61, 0.2) 0%,
      rgba(223, 192, 94, 0.15) 100%);
  border-color: rgba(198, 146, 61, 0.6);
}

.hamburger-btn .material-symbols-rounded {
  font-size: 24px;
}

/* Mobile Overlay */
.mobile-overlay {
  display: none;
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1999;
  backdrop-filter: blur(4px);
}

/* Media queries para móvil */
@media (max-width: 768px) {
  .hamburger-btn {
    display: inline-flex;
  }

  .brand {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
  }

  .nav {
    position: fixed;
    top: 68px;
    left: 0;
    width: 280px;
    max-width: 80vw;
    height: calc(100vh - 68px);
    background: var(--panel-bg);
    border-right: 1px solid var(--panel-border);
    box-shadow: 4px 0 24px rgba(0, 0, 0, 0.15);
    backdrop-filter: blur(16px);
    padding: 1rem;
    flex-direction: column;
    align-items: stretch;
    gap: 0.5rem;
    overflow-y: auto;
    transform: translateX(-100%);
    transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    z-index: 2001;
  }

  .nav.nav-mobile-open {
    transform: translateX(0);
  }

  .mobile-overlay {
    display: block;
  }

  .nav-link {
    width: 100%;
    justify-content: flex-start;
    padding: 0.75rem 1rem;
  }

  .nav-search {
    width: 100%;
    margin-left: 0;
    order: -1;
  }

  .nav-input {
    width: 100%;
    max-width: 100%;
  }

  .user-menu {
    width: 100%;
    flex-direction: row-reverse;
    justify-content: flex-end;
  }

  .user-name {
    display: inline;
  }

  .shell {
    padding-top: 68px;
  }

  .topbar {
    flex-direction: row;
    padding: 0.75rem 1rem;
  }
}
</style>
// const debugMsg = ref("");
// const debugVisible = ref(false);
// onMounted(async () => {
// try {
// await listen("debug-error", (e) => {
// const p = e?.payload;
// const msg = typeof p === "string" ? p : (p && (p.message || p.area)) || "";
// debugMsg.value = String(msg || "");
// debugVisible.value = !!debugMsg.value;
// });
// } catch {}
// });
